import type { Sync, UnwrapDataWithResponseInit } from '@legalzoom/types';
import React, { createContext, useContext } from 'react';
import {
  type LoaderFunctionArgs,
  type useLoaderData,
  useRouteLoaderData,
} from 'react-router';
import type { RootLoader } from './root.server';

export type LoaderData<TLoader> = Sync<
  TLoader extends (...args: LoaderFunctionArgs[]) => LoaderFunctionArgs
    ? ReturnType<TLoader>
    : never
>;

/**
 * Create loader data utilities without deferred/promise handling.
 */
export const loaderData = <
  TLoader extends (...args: LoaderFunctionArgs[]) => unknown,
>(
  route: string,
) => {
  type LoaderDataType = LoaderData<TLoader>;
  const Context = createContext<LoaderDataType>(
    null as unknown as LoaderDataType,
  );

  type LoaderDataProviderProps = {
    children: React.ReactNode;
    properties?: string[] | readonly string[];
  };

  /**
   * @deprecated To be removed completely in the near future
   */
  function LoaderDataProvider({ children }: LoaderDataProviderProps) {
    const data = useRouteLoaderData(route) as LoaderDataType;

    if (!data) {
      /**
       * Fallback in case no data
       * Not having this results in the application throwing
       * a 500 due to consumers destructuring properties that won't exist once error boundaries are invoked
       */
      return null;
    }

    return <Context.Provider value={data}>{children}</Context.Provider>;
  }

  return {
    Context,

    AwaitLoaderData: LoaderDataProvider,

    useRouteLoaderData() {
      return useRouteLoaderData<TLoader>(route) as ReturnType<
        typeof useLoaderData<TLoader>
      >;
    },
    /**
     * @deprecated To be removed completely in the near future
     */
    useLoaderData() {
      return useContext(Context);
    },
  };
};

/* We need to unwrap the data from the response init to get the actual data */
export type AuthenticatedLoaderData = Extract<
  UnwrapDataWithResponseInit<Awaited<ReturnType<RootLoader>>>,
  { isPublic: false }
>;
export const isAuthenticatedLoaderData = (
  loaderData: unknown,
): loaderData is AuthenticatedLoaderData => {
  return (
    typeof loaderData === 'object' &&
    !!loaderData &&
    'isPublic' in loaderData &&
    loaderData.isPublic === false
  );
};
